import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { get, isEmpty, map, filter, split, includes } from 'lodash';
import { connect } from 'react-redux';
import Payments from '../../../api/api-payments';
import { Link } from 'react-router';
import { CreditCardForm } from '../elements-forms/credit-card';
import currencySym from 'currency-symbol-map';
import CTABanner from 'shared-components/banners/CTABanner';
import bankStatusData from './data/bankStatusData';
import verificationStatusData from './data/verificationStatusData';
import { FormattedMessage, injectIntl, intlShape, defineMessages } from 'react-intl';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import AddressFormatter from '@shopify/address';
import _ from 'lodash';
import { TaxInformationSection } from './tax-information/TaxInformationSection';
import {
  getTaxIdByCountryAndVariant,
  getTaxStatusByAccountDetails,
  getVatIdByCountry,
  getVatStatusByAccountDetails
} from './tax-information/tax-information.utils';
import { TAX_ID_VARIANTS } from './tax-information/tax-information.constants';
import { withIDV } from './idv/with-idv.hoc';

const translationMessages = defineMessages({
  errorMessage: {
    id: 'hosting.payments_summary.error_message',
    defaultMessage: 'Something went wrong, please try again.'
  }
});

class PaymentsSummary extends Component {
  constructor (props) {
    super(props);

    this.state = {
      optimisticCards: null,
      displayError: '',
      billingAddress: null,
      orderedAddressFields: null,
      countryData: null
    };

    this.viewVerificationDetails = this.viewVerificationDetails.bind(this);
    this.submitForm = this.submitForm.bind(this);
    this.addCard = this.addCard.bind(this);
    this.toggleCC = this.toggleCC.bind(this);
    this.callback = this.callback.bind(this);
    this.formatBillingAddress = this.formatBillingAddress.bind(this);
    this.shouldRenderCTABanner = this.shouldRenderCTABanner.bind(this);
  }

  componentDidMount () {
    this.setState({ optimisticCards: this.props.cards });
  }

  componentWillMount () {
    this.formatBillingAddress();
  }

  componentDidUpdate (prevProps) {
    const { fee_profiles } = this.props;
    const { fee_profiles: prevFeeProfiles } = prevProps;

    if (prevFeeProfiles.billingAddress.isFetching && !fee_profiles.billingAddress.isFetching) {
      this.formatBillingAddress();
    }
  }

  formatBillingAddress () {
    const { fee_profiles } = this.props;

    if (fee_profiles.billingAddress.data) {
      const billingInfo = get(fee_profiles, 'billingAddress.data', []);
      const hasBillingAddress = !isEmpty(billingInfo);

      if (hasBillingAddress) {
        const billingAddress = filter(billingInfo, { obj_state: 'ACTIVE' })[0];

        const { street = '', unit = '', city = '', state = '', country = '', zip = '' } = billingAddress;
        this.setState({ billingAddress: billingAddress });
        const locale = split(SharedIntlProviderHOC.getLocale(), '-', 1)[0];
        const addressFormatter = new AddressFormatter(locale);

        addressFormatter.getCountry(country)
          .then((countryData => this.setState({ countryData: countryData })));

        addressFormatter.format({
          address1: street,
          address2: unit,
          city: city,
          province: state,
          zip: zip,
          country: country
        }).then(data => this.setState({ orderedAddressFields: data }));
      }
    }
  }

  viewVerificationDetails () {
    const { token, intl } = this.props;
    Payments.getStripeAccountLink('account_update', token, (response) => {
      const verificationLink = get(response, 'url', '');
      if (response.error || !verificationLink) {
        return this.setState({ displayError: intl.formatMessage(translationMessages.errorMessage) });
      }
      location.href = verificationLink;
    });
  }

  submitForm (response, formCallback) {
    if (response.error) {
      this.setState({ cardError: response.error.message });
      formCallback(true);
    } else {
      this.addCard(response, formCallback);
    }
  }

  toggleCC () {
    const { showCC } = this.state;

    this.setState({
      showCC: !showCC,
      cardError: ''
    });
    const form = _.result(this.refs.add_card_form, 'getWrappedInstance', this.refs.add_card_form); // check if its wrapped, if so get wrapped instance
    form.clearForm();
  }

  callback (error) {
    if (!error) {
      this.setState({
        showCC: false
      });
    }
  }

  addCard (cardTokenObj, formCallback) {
    const {
      profile,
      token,
      env,
      intl
    } = this.props;

    const cardToken = (cardTokenObj || {}).token || {};

    Payments.addCard(profile.SSOid, token, cardToken.id, (response) => {
      // common error handler
      const handleError = (errorMessage) => { // uses same "this" as the outside of this function
        const cardError = errorMessage || intl.formatMessage(translationMessages.errorMessage);
        this.setState({ cardError }, formCallback(true));
      };

      // common success handler
      const handleSuccess = () => { // uses same "this" as the outside of this function
        const newState = {
          optimisticCards: this.state.optimisticCards.concat(cardToken.card),
          cardError: ''
        };
        this.setState(newState, () => formCallback(false, response));
      };

      if (200 !== response.statusCode) {
        const { intl } = this.props;
        const cardError = get(response, 'error.message') || intl.formatMessage(translationMessages.errorMessage);
        return this.setState({
          cardError
        }, formCallback(true));
      }

      if (!response.client_action_required) { // no additional client side action is required
        handleSuccess();
        return;
      }

      // additional action on the client side is required
      const stripeV3 = Stripe(env.web.stripe.stripe_public_key);
      const paymentMethodId = response.id;
      stripeV3
        .confirmCardSetup(
          response.client_action_client_secret,
          { payment_method: paymentMethodId }
        )
        .then((result) => {
          result.error
            ? handleError(intl.formatMessage({ id: 'hosting.payment_summary.authentication_error_message', defaultMessage: 'We were unable to authenticate your card. Please try again.' }))
            : handleSuccess();
        });
    });
  }

  shouldRenderCTABanner () {
    const {
      fee_profiles
    } = this.props;

    const feeProfileIdHasFetched = get(fee_profiles, 'billingAddress.hasFetched');

    return feeProfileIdHasFetched;
  }

  render () {
    const {
      account,
      isVerified,
      stripeVerificationStatus,
      accountProblems,
      pendingVerification,
      profile,
      hasPersons,
      hasBank,
      cards,
      credits = {},
      bankName,
      acctString,
      intlDataKey,
      showTaxInformationSection,
      taxId,
      vatId,
      taxStatus,
      vatStatus,
      bankAccountReviewRequired
    } = this.props;

    const {
      showCC,
      optimisticCards,
      displayError,
      billingAddress,
      orderedAddressFields,
      countryData
    } = this.state;

    let billingCTAHeader;
    let billingCTABody;

    if (billingAddress && orderedAddressFields) {
      const { country, state } = billingAddress;

      if (orderedAddressFields[2]) {
        billingCTAHeader = orderedAddressFields[2] + (orderedAddressFields[3] ? ` ${orderedAddressFields[3]}` : '');

        // some customization for AU, CA and US to display shortened state/provinces eg. California -> CA
        if (includes(['CA', 'US', 'AU'], country)) {
          const zone = filter(countryData.zones, { code: state })[0];
          const { name, code } = zone;
          billingCTABody = orderedAddressFields[4].replace(name, code) + ', ' + orderedAddressFields[5];
        } if ('GB' === country) {
          billingCTABody = `${orderedAddressFields[4]}, ${orderedAddressFields[5]}, ${orderedAddressFields[6]}`;
        } else {
          billingCTABody = orderedAddressFields[4] + ', ' + orderedAddressFields[5];
        }
      // if user only has country on billing address, only display country on CTA banner (except Canada)
      } else {
        if ('CA' === country) {
          billingCTAHeader = orderedAddressFields[4] + ', ' + orderedAddressFields[5];
        } if ('GB' === country) {
          billingCTAHeader = orderedAddressFields[6];
        } else {
          billingCTAHeader = orderedAddressFields[5];
        }
        billingCTABody = '';
      }
    }

    const cardsToRender = optimisticCards || cards;
    const noCardYet = isEmpty(cardsToRender);
    const missing = get(account, 'combined_requirements') || [];
    const className = this.props.hideHeading ? '' : 'container';
    const psCreditAmount = parseFloat(get(credits, 'balance')).toFixed(2);
    const currencySymbol = currencySym(get(account, 'default_currency', 'USD'));
    // this `external_accounts_error` flag is set when a bank account is updated/created
    // it is set to true when that bank account has an errored status
    const isPayoutsError = get(profile, 'external_accounts_error', false);

    let bankStatus;
    let verificationStatus;

    const initialBankSetUp = !hasBank && !stripeVerificationStatus;

    if (!hasBank && !initialBankSetUp) {
      bankStatus = 'account_needed';
    } else if (accountProblems) {
      bankStatus = 'update_account';
    } else if (isPayoutsError) {
      bankStatus = 'payouts_error';
    } else if (hasBank) {
      bankStatus = 'account_added';
    }

    const persons = _.get(account, 'persons', []);
    const hasIncompletePersons = persons.some(person => 0 < (person.requirements?.currently_due?.length || 0) ||
      0 < (person.requirements?.pending_verification?.length || 0)
    );

    const onlyMissingExternalAccount =
      1 === missing.length && 'external_account' === missing[0];

    const stripeAccountRecentlyUpdated =
      !profile.stripe_account_last_modified ||
      10 > moment().diff(moment.unix(profile.stripe_account_last_modified), 'minutes');

    // PRIORITIZE missing fields over "pending"
    if (!isEmpty(missing) && !onlyMissingExternalAccount) {
      verificationStatus = 'more_information_needed';
    } else if (
      (0 < persons.length && hasIncompletePersons) ||
      (!account.payouts_enabled && stripeAccountRecentlyUpdated)
    ) {
      verificationStatus = 'pending_verification';
    } else if (!account.payouts_enabled && !onlyMissingExternalAccount) {
      verificationStatus = 'payouts_disabled';
    } else {
      verificationStatus = 'account_verified';
    }

    const bankData = bankStatusData(bankName, acctString)[bankStatus];

    // Disable button when bank account review is required
    const modifiedBankData = bankAccountReviewRequired
      ? { ...bankData, isDisabled: true }
      : { ...bankData, isDisabled: false };

    const handleBankingCTAClick = () => {
      const { idv, hasBank } = this.props;

      // Do not block user from adding banking info
      // IDV should protect only existing records
      if (!hasBank) {
        location.href = '/account/bank';
        return;
      }

      // If idv object is missing then developer forgot to wrap this component with withIDV hoc
      if (!idv) {
        throw new Error('PaymentsSummary must be wrapped within withIDV HOC');
      }

      const successAction = () => {
        bankData.onClickAction();
      };

      const { hasValidVerification, showIDVModal } = idv;

      if (hasValidVerification) {
        return successAction();
      }

      showIDVModal({
        onSuccess: successAction,
        closable: true
      });
    };

    return (
      <div className={`${className} PaymentsSummary`}>
        {
          !this.props.hideHeading &&
            <h3 className="heading">
              <FormattedMessage
                id="hosting.payments_summary.heading"
                defaultMessage="Payments"
              />
            </h3>
        }
        { (profile.is_host || profile.account_id) &&
          <section className="ps-row-nested">
            <div className="col-xs-12 col-md-8 no-gutter">
              <div>
                <h5>
                  <FormattedMessage
                    id="hosting.payments_summary.direct_deposit_heading"
                    defaultMessage="Direct Deposit"
                  />
                </h5>
                <hr className="margin-lg-bottom margin-lg-top" />
              </div>

              { (initialBankSetUp || !bankStatus) ?
                <div className="margin-xl-bottom text-gray-600">
                  <p>
                    <FormattedMessage
                      id="hosting.payments_summary.bank_setup_text"
                      defaultMessage="Add a bank account so you can get paid after completing a booking."
                    />
                  </p>

                  <Link to={{ pathname: '/account/host' }}>
                    <button className="btn btn-primary col-xs-12 col-sm-2 col-md-3 col-lg-2 margin-xl-bottom margin-md-top">
                      <FormattedMessage
                        id="hosting.payments_summary.bank_setup_button_text"
                        defaultMessage="Get started"
                      />
                    </button>
                  </Link>
                </div> :
                <div>
                  <div className="margin-xl-bottom text-gray-600">
                    <p>
                      <FormattedMessage
                        id="hosting.payments_summary.payout_text"
                        defaultMessage="All payouts will be paid to the following account."
                      />
                    </p>
                  </div>

                  {bankAccountReviewRequired &&
                    <div className="margin-xl-bottom text-gray-600">
                      <p>
                        <FormattedMessage
                          id="hosting.payments_summary.payout_paused_text"
                          defaultMessage="Thank you for completing identity verification. While our team reviews your submission, payouts are temporarily paused. You'll receive email confirmation once this process is complete and payouts are updated to your new bank account - please allow up to 48 hours."
                        />
                      </p>
                    </div>
                  }

                  <div>
                    <CTABanner {...modifiedBankData} onClickAction={handleBankingCTAClick} />
                    { verificationStatus && <CTABanner {...verificationStatusData(this.viewVerificationDetails)[verificationStatus]} /> }
                    <div className="help-block text-danger text-left">{ displayError }</div>
                  </div>
                </div>
              }
            </div>
          </section>
        }

        {showTaxInformationSection ?
          <section className="ps-row-nested tw-mb-16">
            <div className="col-xs-12 col-md-8 no-gutter ">
              <h5>
                <FormattedMessage
                  id="hosting.payments_summary.tax_information"
                  defaultMessage="Tax Information"
                />
              </h5>
              <hr className="margin-lg-bottom margin-lg-top" />
              <p className="margin-xl-bottom text-gray-600">
                <FormattedMessage
                  id="hosting.payments_summary.manage_tax_information"
                  defaultMessage="Manage your tax identification number for accurate tax reporting and compliance."
                />
              </p>
              <div className="border tw-p-16 tw-rounded-2 tw-bg-gray-100">
                <TaxInformationSection
                  taxId={taxId}
                  vatId={vatId}
                  taxStatus={taxStatus}
                  vatStatus={vatStatus}
                />
              </div>
            </div>
          </section>
          : null
        }

        <section>
          <div className="ps-row-nested">
            <div className="col-xs-12 col-md-8">
              <h5>
                <FormattedMessage
                  id="hosting.payments_summary.credit_card_heading"
                  defaultMessage="Credit Cards"
                />
              </h5>
              <hr className="margin-lg-bottom margin-lg-top" />
            </div>
            <div className="col-xs-12 col-md-8 text-gray-600">
              <p>
                <FormattedMessage
                  id="hosting.payments_summary.add_credit_card_text"
                  defaultMessage="Adding a credit card allows Peerspace to charge you for reserved bookings. This information is securely sent to our payment provider and never stored by Peerspace."
                />
              </p>
            </div>
          </div>
          <div className="ps-row-nested">
            <div className="col-xs-12 col-md-6">
              {
                map(cardsToRender, (card, i) => {
                  return (
                    <div key={`card-${i}`} className="gray-banner-container row">
                      <div className="col-xs-12">
                        {`${card.brand} - ${card.last4} (Exp. ${card.exp_month}/${card.exp_year})`}
                      </div>
                    </div>
                  );
                })
              }
              {
                !isEmpty(cardsToRender) && !showCC ?
                  <span className="link-span margin-md-top underline" onClick={this.toggleCC}>
                    <FormattedMessage
                      id="hosting.payments_summary.add_new_card_text"
                      defaultMessage="Add new card"
                    />
                  </span>
                  : null
              }

              <CreditCardForm
                showForm={noCardYet || showCC}
                callback={this.callback}
                submitForm={this.submitForm}
                errorText={this.state.cardError}
                toggle={this.toggleCC}
                ref="add_card_form"
                hideCancel={noCardYet}
              />

            </div>
          </div>
        </section>

        { 0 < psCreditAmount &&
          (<section>
            <div className="ps-row-nested">
              <div className="col-xs-12 col-md-8 margin-lg-top">
                <h5>
                  <FormattedMessage
                    id="hosting.payments_summary.peerspace_credit_heading"
                    defaultMessage="Peerspace Credit"
                  />
                </h5>
                <hr className="margin-lg-bottom margin-lg-top" />
              </div>

              <div className="col-xs-12 col-md-8 ">
                <div className="text-gray-600 margin-xl-bottom">
                  <FormattedMessage
                    id="hosting.payments_summary.peerspace_credit_text"
                    defaultMessage="Credits are valid for bookings in the United States."
                  />
                </div>
                <div className="flex space-between border" style={{ padding: 12, borderRadius: 5 }}>
                  <div className="heavy flex center">
                    <img className="margin-md-right" src="https://assets.peerspace.com/ps-credit-icon.svg?v=rb" />
                    <span>
                      <FormattedMessage
                        id="hosting.payments_summary.credit_balance_text"
                        defaultMessage="Credit balance"
                      />
                    </span>
                  </div>
                  <div className="text-green-200">
                    <b>{currencySymbol}{psCreditAmount}</b>
                  </div>
                </div>
              </div>
            </div>
          </section>)
        }
        <section className="ps-row-nested">
          <div className="col-xs-12 col-md-8 no-gutter margin-lg-top">
            <div>
              <h5>
                <FormattedMessage
                  id="hosting.payments_summary.billing_address_heading"
                  defaultMessage="Billing Address"
                />
              </h5>
              <hr className="margin-lg-bottom margin-lg-top" />
            </div>

            <div>
              <div className="margin-xl-bottom text-gray-600">
                <p>
                  <FormattedMessage
                    id="hosting.payments_summary.billing_address_text"
                    defaultMessage="Used when processing payouts and payments."
                  />
                </p>
              </div>

              <div>
                {this.shouldRenderCTABanner() && billingAddress ?
                  <CTABanner
                    headerNotBold={true}
                    header={billingCTAHeader}
                    body={billingCTABody}
                    buttonText={<FormattedMessage id="hosting.payments_summary.billing_address_update_button_text" defaultMessage="Update"/>}
                    alertState={false}
                    onClickAction={() => { location.href = '/account/billing'; }}/>
                  : <CTABanner
                    icon="signage"
                    header={<FormattedMessage id="hosting.payments_summary.billing_address_add_header" defaultMessage="Add billing address"/>}
                    buttonText={<FormattedMessage id="hosting.payments_summary.billing_address_add_button_text" defaultMessage="Add"/>}
                    alertState={true}
                    onClickAction={() => { location.href = '/account/billing'; }}/>}
                <div className="help-block text-danger text-left">{ displayError }</div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

PaymentsSummary.propTypes = {
  hideHeading: PropTypes.bool,
  bankAccountDisplay: PropTypes.element,
  iconVerify: PropTypes.string,
  verifyText: PropTypes.string,
  buttonText: PropTypes.string,
  isVerified: PropTypes.bool,
  isPending: PropTypes.bool,
  profile: PropTypes.object,
  cards: PropTypes.array,
  token: PropTypes.string,
  hasAcct: PropTypes.string,
  hasBank: PropTypes.bool,
  account: PropTypes.object,
  credits: PropTypes.object,
  intl: intlShape,
  stripeVerificationStatus: PropTypes.string,
  accountProblems: PropTypes.bool,
  pendingVerification: PropTypes.bool,
  hasPersons: PropTypes.bool,
  bankName: PropTypes.string,
  acctString: PropTypes.string,
  hasBillingAddress: PropTypes.bool,
  billingInfo: PropTypes.array,
  env: PropTypes.object,
  fee_profiles: PropTypes.object,
  bankAccountReviewRequired: PropTypes.bool
};

function mapStateToProps (state) {
  const { fee_profiles, nav, space, profile } = state;

  const country = space?.countryStatus?.data?.country || 'US';

  const account = nav?.managedAccount;
  const taxInformationVariant = account?.business_type;

  const taxId = getTaxIdByCountryAndVariant(country, taxInformationVariant);
  const taxStatus = getTaxStatusByAccountDetails(account);

  const vatId = taxInformationVariant === TAX_ID_VARIANTS.COMPANY ? getVatIdByCountry(country) : null;
  const vatStatus = getVatStatusByAccountDetails(account);

  const showTaxInformationSection = !!taxId || !!vatId;

  const bankAccountReviewRequired = profile?.data?.bank_account_review_required;

  return {
    fee_profiles,
    showTaxInformationSection,
    taxInformationVariant,
    taxId,
    vatId,
    taxStatus,
    vatStatus,
    bankAccountReviewRequired
  };
}

export default connect(mapStateToProps)(injectIntl(withIDV(PaymentsSummary)));
